import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Mail } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import SectionLabel from "@/components/SectionLabel";

/**
 * Post-checkout success page. Stripe redirects here with ?session_id=...
 * We poll /api/public/digital-purchase/by-session/{session_id} every 2s
 * until the webhook has fired and the purchase doc exists.
 */
export default function DownloadSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [status, setStatus] = useState("polling"); // polling | ready | error | timeout
  const [error, setError] = useState("");
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setError("No checkout session found in URL.");
      return;
    }
    let cancelled = false;
    let count = 0;

    const poll = async () => {
      try {
        const { data } = await api.get(`/public/digital-purchase/by-session/${sessionId}`);
        if (cancelled) return;
        if (data?.ready && data.token) {
          setStatus("ready");
          // Brief delay so the user sees the success state before we navigate
          setTimeout(() => navigate(`/download/${data.token}`, { replace: true }), 800);
          return;
        }
      } catch (err) {
        if (cancelled) return;
        setError(formatApiError(err?.response?.data?.detail) || "Could not verify purchase.");
        setStatus("error");
        return;
      }
      count += 1;
      setAttempts(count);
      if (count > 20) {
        // ~40s timeout — webhook should fire in <10s normally
        setStatus("timeout");
        return;
      }
      setTimeout(poll, 2000);
    };

    poll();
    return () => {
      cancelled = true;
    };
    // `cancelled` and `count` are intentionally function-scoped, not deps.
    // `api`/`formatApiError` are stable module imports.
  }, [sessionId, navigate]);

  return (
    <div className="py-24 lg:py-32" data-testid="download-success-page">
      <section className="max-w-[800px] mx-auto px-6 lg:px-12 text-center space-y-7">
        <SectionLabel label="Payment Received" />
        <h1 className="font-serif font-light tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
          Thanks. Track's on its way.
        </h1>

        {status === "polling" && (
          <div
            className="surface p-8 flex flex-col items-center gap-4"
            data-testid="download-success-polling"
          >
            <Loader2 size={32} className="animate-spin" style={{ color: "var(--gold)" }} />
            <p className="text-white/70">
              Confirming your purchase with Stripe… ({attempts + 1}/20)
            </p>
            <p className="text-[10px] uppercase tracking-[0.22em] text-white/40">
              Hold tight — usually under 10 seconds
            </p>
          </div>
        )}

        {status === "ready" && (
          <div
            className="surface p-8 flex flex-col items-center gap-4"
            data-testid="download-success-ready"
          >
            <CheckCircle2 size={36} style={{ color: "var(--gold)" }} />
            <p className="text-white/80 text-lg">Confirmed. Loading your locker…</p>
          </div>
        )}

        {status === "timeout" && (
          <div
            className="surface p-8 flex flex-col items-center gap-4"
            data-testid="download-success-timeout"
          >
            <Mail size={32} style={{ color: "var(--gold)" }} />
            <p className="text-white/85 max-w-md leading-relaxed">
              Stripe is taking longer than usual to confirm. Check your email — your download
              link is on the way. If it doesn't arrive within 5 minutes, email{" "}
              <a
                href="mailto:bookings@ninacapone.com"
                className="text-[color:var(--gold)] hover:underline"
              >
                bookings@ninacapone.com
              </a>{" "}
              with your Stripe receipt.
            </p>
            <Link
              to="/shop"
              className="text-[10px] uppercase tracking-[0.25em] text-white/55 hover:text-[color:var(--gold)] transition"
            >
              ← Back to Shop
            </Link>
          </div>
        )}

        {status === "error" && (
          <div
            className="surface p-8 flex flex-col items-center gap-4"
            data-testid="download-success-error"
          >
            <p className="text-white/85">{error}</p>
            <Link
              to="/shop"
              className="text-[10px] uppercase tracking-[0.25em] text-[color:var(--gold)] hover:underline"
            >
              ← Back to Shop
            </Link>
          </div>
        )}
      </section>
    </div>
  );
}
